import {
  updateShippingAddress,
  validateAddress,
} from "@/api/rest/authenticated/Address";
import { ShippingAddress } from "@/components/Checkout/types";
import GenericDialog from "@/components/StoreComponents/StoreDialogs/GenericDialog";
import { StoreError } from "@/components/StoreComponents/StoreError";
import { useAppSelector } from "@/redux/hooks";
import { useState } from "react";
import CheckoutEditShippingForm, {
  isAddressEqual,
} from "../../../CheckoutSummary/CheckoutEditShippingForm";
import { useCheckoutUI } from "../../CheckoutUIProvider";
import { CheckoutIntent } from "../../types/CheckoutIntent";

export const ShippingMethodForm = () => {
  const [machineState, send] = useCheckoutUI();

  const user = useAppSelector((state) => state.userProfile);

  const [addressError, setAddressError] = useState<string | null>(null);

  const { shippingInput, checkoutIntent } = machineState.context;

  const { firstName, lastName, address1, address2, city, state, zipCode } =
    shippingInput || {};

  const handleValidAddress = async (
    shippingInput: CheckoutIntent["shippingAddress"]
  ) => {
    if (!shippingInput || !checkoutIntent?.checkoutIntentId) return;

    const { checkoutIntent: updatedCheckoutIntent, status } =
      await updateShippingAddress({
        shippingAddress: shippingInput,
        checkoutIntentId: checkoutIntent.checkoutIntentId,
      });

    if (status === "success") {
      // First update checkout intent data
      send({
        type: "SET_CHECKOUT_INTENT",
        data: { checkoutIntent: updatedCheckoutIntent },
      });

      // Mark shipping section as valid and completed
      send({ type: "SET_IS_VALID_ADDRESS", data: true });
    } else {
      setAddressError(
        "An error occurred while updating your address. Please try again."
      );
    }
  };

  const handleSave = async () => {
    // Clear previous errors
    setAddressError(null);

    // Basic form validation
    if (!address1 || !city || !state || !zipCode || !firstName || !lastName) {
      setAddressError("Please fill in all required address fields");
      return;
    }

    try {
      // Validate the address with the API
      const validationResult = await validateAddress({
        address1: address1,
        address2: address2 || "",
        city: city,
        state: state,
        zipCode: zipCode,
        firstName: firstName,
        lastName: lastName,
        country: "US",
        countryCode: "US",
      });

      if (validationResult.status === "failed" || !validationResult.isValid) {
        send({ type: "SET_IS_VALID_ADDRESS", data: false });
        setAddressError(
          validationResult.errorMessage ||
            "Invalid address. Please check your address details."
        );
        return;
      } else {
        send({ type: "SET_IS_VALID_ADDRESS", data: true });
      }

      // Check if the validated address is different from the input
      const formattedAddress =
        validationResult.validationResult?.result?.address?.formattedAddress;
      const formattedAddressLines =
        validationResult.validationResult?.result?.address?.postalAddress
          ?.addressLines || [];
      const formattedCity =
        validationResult.validationResult?.result?.address?.postalAddress
          ?.locality;
      const formattedState =
        validationResult.validationResult?.result?.address?.postalAddress
          ?.administrativeArea;
      const formattedZip =
        validationResult.validationResult?.result?.address?.postalAddress
          ?.postalCode;

      const isAddressDifferent =
        (formattedAddressLines.length > 0 &&
          formattedAddressLines[0].toLowerCase() !== address1.toLowerCase()) ||
        (formattedAddressLines.length > 1 &&
          formattedAddressLines[1].toLowerCase() !==
            (address2 || "").toLowerCase()) ||
        (formattedCity && formattedCity.toLowerCase() !== city.toLowerCase()) ||
        (formattedState &&
          formattedState.toLowerCase() !== state.toLowerCase()) ||
        (formattedZip && formattedZip !== zipCode);

      if (isAddressDifferent && formattedAddress) {
        // Show dialog to confirm which address to use
        GenericDialog.show({
          title: "Address Suggestion",
          text: `We found a more accurate format for your address. Would you like to use the suggested address?

Original
${address1}${address2 ? `\n${address2}` : ""}
${city}, ${state} ${zipCode}

Suggested
${formattedAddressLines[0] || ""}${
            formattedAddressLines.length > 1
              ? `\n${formattedAddressLines[1]}`
              : ""
          }
${formattedCity || city}, ${formattedState || state} ${
            formattedZip || zipCode
          }`,
          textProps: {
            sx: {
              whiteSpace: "pre-line",
              fontFamily: "monospace",
              fontSize: "14px",
              lineHeight: "1.5",
            },
          },
          actions: [
            {
              text: "Use Original",
              variant: "outline",
              onClick: () => {
                handleValidAddress(shippingInput);
              },
            },
            {
              text: "Use Suggested",
              variant: "solid",
              onClick: () => {
                // Update shipping with formatted address
                if (formattedAddressLines.length > 0) {
                  const updatedShipping = {
                    firstName,
                    lastName,
                    address1: formattedAddressLines[0] || address1,
                    address2:
                      formattedAddressLines.length > 1
                        ? formattedAddressLines[1]
                        : "",
                    city: formattedCity || city,
                    state: formattedState || state,
                    zipCode: formattedZip || zipCode,
                    country: "US",
                  };

                  send({
                    type: "SET_SHIPPING",
                    data: updatedShipping,
                  });

                  send({
                    type: "SET_IS_VALID_ADDRESS",
                    data: true,
                  });

                  handleValidAddress(updatedShipping);
                }
              },
            },
          ],
        });
      } else {
        handleValidAddress(shippingInput);
      }
    } catch (error) {
      setAddressError(
        "An error occurred while validating your address. Please try again."
      );
      console.error("Address validation error:", error);
    }
  };

  const updateShipping = async (
    data: Partial<CheckoutIntent["shippingAddress"]>
  ) => {
    if (
      checkoutIntent &&
      !isAddressEqual(checkoutIntent?.shippingAddress, data as ShippingAddress)
    ) {
      // Clear error when user updates any field
      setAddressError(null);
      send({ type: "RESET_IS_VALID_ADDRESS" });
      send({ type: "SET_SHIPPING", data });
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <CheckoutEditShippingForm
        availableAddresses={(user?.addresses ?? []) as ShippingAddress[]}
        handleSaveShippingAddress={handleSave}
        onChange={updateShipping}
        existingAddress={
          checkoutIntent?.shippingAddress
            ? ({
                ...checkoutIntent?.shippingAddress,
                extendedAttributes: {
                  isSelected: true,
                },
              } as ShippingAddress)
            : undefined
        }
      />
      {addressError && <StoreError errorMessage={addressError} />}
    </div>
  );
};
